<template>
  <el-dialog
    :visible.sync="innerVisible"
    :title="hasInterviews ? title : ''"
    width="480px"
    :close-on-click-modal="false"
    custom-class="submit-dialog"
    :modal="true"
    :append-to-body="true"
    :lock-scroll="true"
    :show-close="false"
    @close="handleClose"
  >
    <template slot="title">
      <div class="dialog-title" v-if="hasInterviews">
        <span class="title-text">{{ title }}</span>
        <img src="../../assets/image/close.svg" alt="关闭图标" @click="handleClose"/>
      </div>
    </template>
    <div class="submit-content" :class="containerClass">
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-text">正在加载材料...</div>
      </div>

      <template v-else>
        <div class="section" v-if="hasInterviews">
          <div class="material-list" ref="materialList" @scroll="handleScroll">
            <div
              v-for="interview in interviewList"
              :key="interview.interview_id"
              class="material-item"
              :class="{ 
                active: selectedInterviewId === interview.interview_id,
                disabled: interview.has_delivered
              }"
              @click="!interview.has_delivered && (selectedInterviewId = interview.interview_id)"
            >
              <div class="material-icon"></div>
              <div class="material-info">
                <div class="material-name-row">
                  <span class="material-name">{{ interview.interview_name || interview.name || '面试记录' }}</span>
                  <span v-if="interview.has_delivered" class="delivered-tag">已投递</span>
                </div>
                <div class="material-date">
                  <span v-if="interview.interview_position" class="position-text">{{ interview.interview_position }}</span>
                  <span>{{ formatInterviewDate(interview.create_time || interview.createTime) }} 面试存档</span>
                </div>
              </div>
              <div class="material-radio">
                <input 
                  type="radio" 
                  :name="'interview'" 
                  :checked="selectedInterviewId === interview.interview_id"
                  :disabled="interview.has_delivered"
                />
              </div>
            </div>
            <!-- 加载更多提示 -->
            <div v-if="loadingMore" class="loading-more">
              <div class="loading-spinner-small"></div>
              <span>加载中...</span>
            </div>
            <div v-if="!hasMore && interviewList.length > 0" class="no-more">没有更多了</div>
          </div>
        </div>
        <div class="empty-block" v-else>
          <div class="empty-icon"><img :src="noFoundImg" alt="暂无数据"></div>
          <div class="empty-title">暂无面试记录</div>
          <div class="empty-desc">去创建一场面试后再来投递吧</div>
        </div>
      </template>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose" :disabled="submitting">取消</el-button>
      <el-button type="primary" :disabled="!canSubmit || submitting" v-if="hasInterviews" @click="startSubmit">
        {{ submitting ? '提交中...' : '立即投递' }}
      </el-button>
      <el-button type="primary" class="button-color" v-else @click="goToInterview">立即面试</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getUserDeliveryInterviewList, createDelivery } from '@/api/mianshilishi'

export default {
  name: 'SubmitMaterialDialog',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '请选择面试记录' },
    job: { type: Object, default: null }
  },
  data () {
    return {
      innerVisible: false,
      loading: false,
      submitting: false,
      interviewList: [],
      selectedInterviewId: null,
      noFoundImg: require('@/assets/image/noFound.png'),
      // 分页相关
      pageNo: 1,
      pageSize: 10,
      hasMore: true,
      loadingMore: false
    }
  },
  computed: {
    canSubmit () {
      return !!this.selectedInterviewId
    },
    containerClass () {
      return 'single-section'
    },
    hasInterviews () {
      return Array.isArray(this.interviewList) && this.interviewList.length > 0
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (v) {
        this.innerVisible = v
        if (v) this.loadData()
      }
    },
    innerVisible (v) {
      this.$emit('update:visible', v)
    }
  },
  methods: {
    async loadData (isLoadMore = false) {
      if (isLoadMore) {
        if (this.loadingMore || !this.hasMore) return
        this.loadingMore = true
      } else {
        this.loading = true
        this.interviewList = []
        this.selectedInterviewId = null
        this.pageNo = 1
        this.hasMore = true
      }
      
      try {
        const resI = await getUserDeliveryInterviewList({ 
          pageNo: this.pageNo, 
          pageSize: this.pageSize,
          jobId: this.job.id,
          companyId: this.job.company_id
        })
        if (resI && resI.data && resI.data.code === 1) {
          const lists = resI.data.data?.lists || resI.data.data || []
          const total = resI.data.data?.count || 0
          
          if (isLoadMore) {
            this.interviewList = [...this.interviewList, ...lists]
          } else {
            this.interviewList = lists
          }
          
          // 判断是否还有更多数据
          this.hasMore = this.interviewList.length < total
          if (lists.length > 0) {
            this.pageNo++
          }
        }
      } catch (e) {
        this.$message.error('加载材料失败，请重试')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    handleScroll (e) {
      const target = e.target
      const scrollTop = target.scrollTop
      const scrollHeight = target.scrollHeight
      const clientHeight = target.clientHeight
      
      // 触底加载：距离底部50px时触发
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        this.loadMoreData()
      }
    },
    loadMoreData () {
      if (!this.loadingMore && this.hasMore) {
        this.loadData(true)
      }
    },
    handleClose () {
      this.innerVisible = false
    },

    async startSubmit () {
      if (!this.canSubmit || this.submitting) return
      if (!this.job) {
        this.$message.warning('暂无可投递职位')
        return
      }
      
      this.submitting = true
      
      try {
        const selectedInterview = this.interviewList.find(i => i.interview_id === this.selectedInterviewId)
        if (!selectedInterview) {
          throw new Error('请选择面试记录')
        }
        
        // 直接调用投递接口
        const deliveryData = {
          job_id: this.job.id,
          interview_id: selectedInterview.interview_id || selectedInterview.id,
          company_id: this.job.company_id,
          receiver_type: 4
        }
        
        const deliveryRes = await createDelivery(deliveryData)
        
        if (deliveryRes.data.code === 1) {
          this.$message.success('投递成功')
          // 通知父组件投递成功
          this.$emit('submit', {
            interviewId: this.selectedInterviewId,
            interview: selectedInterview,
            job: this.job
          })
          this.handleClose()
        } else {
          throw new Error(deliveryRes.data.msg || '投递失败')
        }
      } catch (e) {
        console.error('投递失败:', e)
        this.$message.error(e.message || '投递失败，请重试')
        this.$emit('submit-error', e)
      } finally {
        this.submitting = false
      }
    },
    goToInterview () {
      this.handleClose()
      try {
        this.$router.push({ name: 'interview_ing' })
      } catch (e) {
        this.$router.push('/interview_ing')
      }
    },
    formatInterviewDate (timestamp) {
      if (!timestamp) return ''
      const d = new Date(timestamp)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    formatResumeMeta (resume) {
      const size = resume.size || resume.resume_size || ''
      const time = resume.uploadTime || resume.create_time || ''
      const date = time ? this.formatInterviewDate(time) : ''
      if (date && size) return `${date} · ${size}`
      return date || ''
    }
  }
}
</script>

<style scoped>
.submit-content { padding: 4px 0; }
.section { display: flex; flex-direction: column; }
.section-title { font-size: 14px; font-weight: 600; color: #333; margin: 0 0 8px; }
.material-list { display: flex; flex-direction: column; gap: 10px; overflow-y: auto; }
.single-section .material-list { max-height: 50vh; }
.loading-more, .no-more { 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  padding: 12px; 
  color: #999; 
  font-size: 12px; 
  gap: 8px;
}
.loading-spinner-small { 
  width: 16px; 
  height: 16px; 
  border: 2px solid #f3f3f3; 
  border-top: 2px solid #B16EF9; 
  border-radius: 50%; 
  animation: spin 1s linear infinite; 
}
.material-item { display:flex; align-items:center; padding:12px; border:1px solid #e0e0e0; border-radius:10px; cursor:pointer; transition:all .2s; }
.material-item.active { border-color:#B16EF9; background: rgba(177,110,249,.05); box-shadow: 0 4px 12px rgba(177,110,249,.1); }
.material-item.disabled { 
  opacity: 0.6; 
  cursor: not-allowed; 
  background: #f5f5f5;
}
.material-item.disabled:hover { 
  background: #f5f5f5;
}
.material-icon { width:40px; height:40px; margin-right:12px; background: rgba(177,110,249,.08); border-radius:8px; }
.material-info { flex:1; display:flex; flex-direction:column; }
.material-name-row { 
  display: flex; 
  align-items: center; 
  gap: 8px; 
  margin-bottom: 4px;
}
.material-name { font-size:14px; font-weight:600; color:#333; }
.delivered-tag { 
  display: inline-block;
  padding: 2px 8px;
  font-size: 12px;
  color: #999;
  background: #f0f0f0;
  border-radius: 4px;
  line-height: 1.4;
}
.material-date { font-size:12px; color:#888; display: flex; align-items: center; gap: 8px; }
.position-text {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}
.material-radio { margin-left: 8px; }

.loading-container { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px 20px; min-height:200px; }
.loading-spinner { width:40px; height:40px; border:3px solid #f3f3f3; border-top:3px solid #B16EF9; border-radius:50%; animation: spin 1s linear infinite; margin-bottom:16px; }
@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
.loading-text { font-size:14px; color:#666; text-align:center; }
.empty-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:36px 16px; min-height:200px; text-align:center; }
.empty-icon { font-size:42px; margin-bottom:8px; }
.empty-title { font-size:16px; font-weight:600; color:#333; margin-bottom:6px; }
.empty-desc { font-size:13px; color:#777; margin-bottom:14px; }
</style>

<style scoped>
/* 无遮罩内联模式样式 */
.submit-inline {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  top: 10vh;
  width: 480px;
  max-height: 80vh;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0,0,0,.15);
  display: flex;
  flex-direction: column;
  z-index: 2100;
}
.inline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}
</style>

<style scoped>
/* 统一限制对话框在视口内的高度，并让内容在内部滚动，防止页面不同场景撑到底部 */
/* 统一控制 wrapper 居中与顶部间距，避免落到左下角 */
::v-deep .el-dialog__wrapper {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: center !important;
  box-sizing: border-box;
  
}
::v-deep .el-dialog__body{
    padding: 0 20px;
}
::v-deep .el-dialog__header{
  font-size: 24px;
  color: #656587;
}
.dialog-title{
  display: flex;
  justify-content: space-between;
}
::v-deep .el-dialog__title{
  
  color: #656587;
  width:100%
}
::v-deep .dialog-footer{
    position: sticky;
    bottom: 0;
    background-color: rgba(255, 255, 255, .72);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 1;
    border-top: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0px;
    max-height: 100px;
    min-height: 70px;
    .el-button{
        flex: 1;
    }
}
</style>

